// 📄 components/StepASNDelivery.tsx
import React from 'react';
import { Info, Truck } from 'lucide-react';
import { AsnFormData } from '../../../../../services/commerce/integrationService';

interface StepASNDeliveryProps {
  deliveryType: string;
  referencePlant: string;
  warehouseNumber: string;
  autoDeliveryCreation: boolean;
  adoptionProfile: string;
  onChange: (field: keyof AsnFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepASNDelivery({
  deliveryType, referencePlant, warehouseNumber, autoDeliveryCreation, adoptionProfile, onChange, isInvalid
}: StepASNDeliveryProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Ruta SPRO / Transacción SAP */}
      <div className="bg-teal-500/5 border border-teal-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-teal-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-teal-300 font-bold mr-1.5">VL06I / VL33N</span>
          <span>SAP Menu → Logistics → Logistics Execution → Inbound Process → Goods Receipt → Inbound Delivery Monitor</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-teal-400">4. Monitoreo de Entregas — Inbound Delivery (VL06I / VL33N)</h3>
        <p className="text-sm text-gray-400 mt-1">
          Define los parámetros para la creación automática de la Entrega Entrante a partir del ASN recibido.
          El sistema usará estos valores para determinar el tipo de documento de entrega y la dirección de almacén durante el procesamiento del IDoc.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Tipo de Entrega Entrante <span className="text-red-400">*</span>
          </label>
          <select
            value={deliveryType}
            onChange={(e) => onChange('deliveryType', e.target.value)}
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !deliveryType ? 'border-red-500' : 'border-white/10'
            }`}
          >
            <option value="EL">EL - Entrega Entrante Estándar</option>
            <option value="ELS">ELS - Entrega Entrante de Subcontratación</option>
            <option value="ELK">ELK - Entrega Entrante Consignación</option>
            <option value="ZELI">ZELI - Entrega Z (Personalizada)</option>
          </select>
          <p className="text-[10px] text-gray-500 mt-1 px-1">
            Configurado en SPRO → Logistics Execution → Delivery Types
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Centro de Referencia (Plant) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={referencePlant}
            onChange={(e) => onChange('referencePlant', e.target.value.toUpperCase())}
            placeholder="ej. 1000"
            maxLength={4}
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !referencePlant ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Número de Almacén (WM - opcional)</label>
          <input
            type="text"
            value={warehouseNumber}
            onChange={(e) => onChange('warehouseNumber', e.target.value.toUpperCase())}
            placeholder="ej. 001"
            maxLength={3}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500"
          />
          <p className="text-[10px] text-gray-500 mt-1 px-1">Solo si el almacén opera con Warehouse Management (WM)</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Perfil de Adopción (Adoption Profile)</label>
          <input
            type="text"
            value={adoptionProfile}
            onChange={(e) => onChange('adoptionProfile', e.target.value.toUpperCase())}
            placeholder="ej. ARIBA01"
            maxLength={10}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500"
          />
          <p className="text-[10px] text-gray-500 mt-1 px-1">Perfil de validación de datos de entrega en la interfaz IDoc</p>
        </div>
      </div>

      {/* Toggle creación automática */}
      <div className="bg-white/5 border border-white/5 rounded-xl p-4">
        <label className="flex items-start gap-4 cursor-pointer">
          <div className="relative mt-0.5 flex-shrink-0">
            <input
              type="checkbox"
              checked={autoDeliveryCreation}
              onChange={(e) => onChange('autoDeliveryCreation', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-10 h-6 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-teal-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-teal-400" />
              <span className="text-sm font-medium text-white">Creación Automática de Entrega Entrante</span>
            </div>
            <span className="text-xs text-gray-400 mt-0.5 block">
              Activa la creación automática de la Inbound Delivery al recibir el IDoc DESADV correctamente en el ERP.
              Si está desactivado, la entrega debe crearse manualmente en <code className="bg-black/30 px-0.5 rounded">VL31N</code>.
            </span>
          </div>
        </label>
      </div>

      <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-4 text-teal-300 flex gap-3 text-sm">
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Determinación automática de almacén (WM)</p>
          <p className="text-xs text-teal-400/80 mt-0.5">
            Si el número de almacén está configurado, verifica en <code className="bg-black/30 px-1 rounded">LS02N</code> que
            el sistema lógico del CIG tiene asignado el almacén correcto. Esto evita rechazos por determinación fallida de
            posición de almacén durante el procesamiento automático del IDoc.
          </p>
        </div>
      </div>
    </div>
  );
}
